import type { JSX } from 'react';

import { Waypoint } from 'react-waypoint';

import { Text } from '@change/ds-core-ui/next';

import type { StarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useRecommendedGroupPickerContext } from 'src/app/pages/starterDashboardV2/shared/components';
import type {
	FacebookPost,
	FullPostType,
	PostPreviewSizes,
	RecommendedGroup,
	RecommendedGroupPickerProps,
	RedditPost,
	SharingProps,
} from 'src/app/pages/starterDashboardV2/shared/components';
import {
	extractSharingResource,
	mapToSharingModalProps,
	POST_CONFIGS,
	useMarkModuleAsLoaded,
	useReadyToShareContext,
	useRecommendations,
	useSharingModal,
} from 'src/app/pages/starterDashboardV2/shared/contexts';
import type { PostConfigRow } from 'src/app/pages/starterDashboardV2/shared/contexts';
import type { ReadyToSharePostWithPlatform } from 'src/app/shared/api/readyToShare';
import type { WaypointCallbackArgs } from 'src/app/shared/components/layout/lazy';
import { useIsMobile } from 'src/app/shared/hooks/userAgent';

import { ScrollButtons } from '../shared/ScrollButtons';

import { PlatformFilter } from './components/PlatformFilter';
import { PostPreviewCard } from './components/PostPreviewCard';
import { PostPreviewCardSkeleton } from './components/PostPreviewCardSkeleton';
import { usePlatformFilter, useScroll, useViewTracking } from './hooks';
import type { PlatformType } from './utils/platformGrouping';

const trackingProps = {
	context: 'petition_dashboard_ready_to_share_post' as const,
	viewContext: 'share' as const,
};

type FullPostTypeMap = {
	post: FullPostType;
	story: FullPostType;
};
const postVariantMapping: Record<PlatformType, FullPostTypeMap> = {
	facebook: {
		post: 'facebook_post',
		story: 'facebook_story',
	},
	instagram: {
		post: 'instagram_post',
		story: 'instagram_story',
	},
	reddit: {
		post: 'reddit_post',
		story: 'reddit_post', // Reddit does not have stories, map to post instead
	},
	facebookgroups: {
		post: 'facebook_group_post',
		story: 'facebook_group_post', // Facebook Groups does not have stories, map to post instead
	},
};

function recommendedGroupForPost(
	fullPostType: FullPostType,
	postConfig: PostConfigRow,
	recommendationsData: ReturnType<typeof useRecommendations>['data'],
) {
	if (!postConfig.requiresRecommendedGroup) return undefined;

	const { isLoading: isRecommendationsLoading, hasFacebookGroups, hasRedditGroups } = recommendationsData;

	if (isRecommendationsLoading) return undefined;

	if (fullPostType.startsWith('facebook')) {
		return hasFacebookGroups ? recommendationsData.facebook[0] : undefined;
	}

	return hasRedditGroups ? recommendationsData.reddit[0] : undefined;
}

type RenderPostOrStoryProps = {
	item: { platform: PlatformType; post: ReadyToSharePostWithPlatform };
	isStory: boolean;
	isMobile: boolean;
	petition: StarterDashboardV2PageData['petition'];
	options: {
		previewSize: PostPreviewSizes;
		startSharingFlow: (props: SharingProps) => void;
		startGroupSelection: (
			props: Omit<RecommendedGroupPickerProps, 'isOpen' | 'onGroupSelected' | 'onClose'>,
		) => Promise<RecommendedGroup | undefined>;
	};
	recommendationsData: ReturnType<typeof useRecommendations>['data'];
};

function renderPostOrStory(props: RenderPostOrStoryProps) {
	const {
		item,
		isStory,
		isMobile,
		petition,
		recommendationsData,
		options: { startSharingFlow, startGroupSelection, previewSize },
	} = props;
	const { post, platform } = item;
	const postVariantMap = postVariantMapping[platform];
	const postVariant = isStory ? postVariantMap.story : postVariantMap.post;

	const postConfig = POST_CONFIGS[postVariant];
	const postDisabled = !isMobile && !postConfig.desktopEnabled;
	const recommendedGroup = recommendedGroupForPost(postVariant, postConfig, recommendationsData);

	const sharingResource = extractSharingResource(postVariant, {
		post,
		petition,
		recommendedGroup,
	});

	const sharingProps = mapToSharingModalProps(petition, {
		fullPostType: postVariant,
		recommendedGroup,
		trackingProps,
	});

	const handlePostClick = !postDisabled
		? async () => {
				if (postConfig.requiresGroupSelection) {
					const sharingPlatform = sharingProps.platform;
					const recommendedGroupsList =
						sharingPlatform === 'facebook' ? recommendationsData.facebook : recommendationsData.reddit;
					const selectedGroup = await startGroupSelection({
						platform: sharingProps.platform === 'facebook' ? ('facebook' as const) : ('reddit' as const),
						recommendedGroups: recommendedGroupsList,
					});

					if (selectedGroup) {
						startSharingFlow({ ...sharingProps, recommendedGroup: selectedGroup } as RedditPost | FacebookPost);
					}
				} else {
					startSharingFlow(sharingProps);
				}
			}
		: () => {};

	return (
		<PostPreviewCard
			onClick={handlePostClick}
			fullPostType={postVariant}
			disabled={postDisabled}
			petition={petition}
			sharingResource={sharingResource}
			previewSize={previewSize}
		/>
	);
}

export function ReadyToSharePreDrafted(): JSX.Element | null {
	const isMobile = useIsMobile();
	const {
		data: { petition },
	} = useStarterDashboardV2PageData();
	const { data: contextData } = useReadyToShareContext();
	const { data: recommendationsData } = useRecommendations();

	const { startGroupSelection } = useRecommendedGroupPickerContext();
	const { startSharingFlow } = useSharingModal();

	const {
		data: { selectedPlatform, allPosts, allStories, skeletons },
		actions: { selectPlatform, clearPlatformSelection },
	} = usePlatformFilter(contextData.platformData);
	const {
		data: { canScrollLeft, canScrollRight, scrollContainerRef },
		actions: { handleScroll },
	} = useScroll(selectedPlatform);
	const { trackView } = useViewTracking();

	useMarkModuleAsLoaded('ReadyToSharePreDrafted', contextData.shouldShowReadyToShareExperiment);

	if (!contextData.shouldShowReadyToShareExperiment) {
		return null;
	}

	const previewSize = (isMobile ? 'medium' : 'large') as PostPreviewSizes;

	const baseRenderPostProps = {
		petition,
		isMobile,
		recommendationsData,
		options: {
			previewSize,
			startSharingFlow,
			startGroupSelection,
		},
	};

	return (
		<section className="bg-background-base flex h-full w-full flex-col px-4 py-8 md:px-12 md:pb-12 md:pt-8">
			<Waypoint onPositionChange={(wp: WaypointCallbackArgs) => trackView(wp.currentPosition)} />

			<div className="mb-5">
				<Text className="text-size-heading-xs md:text-size-heading-small">Ready-to-share posts</Text>
			</div>
			<div className="flex justify-between align-middle">
				<PlatformFilter
					selectedPlatform={selectedPlatform}
					onClearSelection={clearPlatformSelection}
					onPlatformSelect={selectPlatform}
				/>
				{/* Scroll Buttons */}
				<ScrollButtons
					canScrollLeft={canScrollLeft}
					canScrollRight={canScrollRight}
					onScrollLeft={() => handleScroll('left')}
					onScrollRight={() => handleScroll('right')}
				/>
			</div>

			{/* Posts Container with Horizontal Scrolling */}
			<div
				className="flex w-full flex-row gap-x-[55px] overflow-x-auto px-1 pb-3 pt-6"
				ref={scrollContainerRef}
				sx={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
			>
				{/* Collect all posts and stories from all platforms */}

				{/* Render all regular posts first */}
				{allPosts.map((item) => (
					<div key={`${item.platform}-${item.post.id}-post`}>
						{renderPostOrStory({ ...baseRenderPostProps, item, isStory: false })}
					</div>
				))}

				{/* Then render all stories */}
				{allStories.map((item) => (
					<div key={`${item.platform}-${item.post.id}-story`}>
						{renderPostOrStory({ ...baseRenderPostProps, item, isStory: true })}
					</div>
				))}

				{/* Show skeletons for loading platforms */}
				{skeletons.map(({ platform }) => {
					const platformResolved = platform === 'facebookgroups' ? 'facebook_group' : platform;
					return (
						<PostPreviewCardSkeleton
							key={`${platform}-post-skeleton`}
							size={previewSize}
							petition={petition}
							fullPostType={`${platformResolved}_post` as FullPostType}
						/>
					);
				})}
			</div>
		</section>
	);
}
